/**
 * Every value one attribute takes on blocks of one type in a document, in the order the blocks
 * appear. The wiki uses it to learn which files and videos a page embeds: `imageBlock` nodes
 * carry a `fileId`, `videoBlock` nodes a `videoId`.
 *
 * @param content - A Tiptap document as the serialised string the column holds.
 * @param type - The node type to look for.
 * @param attr - The attribute to read off each such node.
 * @returns The values, without duplicates. Empty for content that is not a document.
 */
export function collectBlockAttrValues(content: string, type: string, attr: string) {
	let document: unknown;
	try {
		document = JSON.parse(content);
	} catch {
		return [];
	}

	const values = new Set<string>();
	const walk = (node: unknown) => {
		if (!node || typeof node !== "object") return;
		const record = node as { type?: string; attrs?: Record<string, unknown>; content?: unknown[] };
		const value = record.attrs?.[attr];
		if (record.type === type && typeof value === "string" && value) values.add(value);
		if (Array.isArray(record.content)) record.content.forEach(walk);
	};
	walk(document);
	return [...values];
}

/**
 * A document with every block of one type whose attribute holds one value taken out.
 *
 * @param content - A Tiptap document as the serialised string the column holds.
 * @param type - The node type to remove.
 * @param attr - The attribute that identifies the block.
 * @param value - The value the attribute must hold for the block to go.
 * @returns The rewritten document, or null when it does not change or cannot be parsed.
 */
export function stripBlocksByAttrValue(content: string, type: string, attr: string, value: string) {
	let document: unknown;
	try {
		document = JSON.parse(content);
	} catch {
		return null;
	}

	let removed = false;
	const prune = (node: unknown): unknown => {
		if (!node || typeof node !== "object") return node;
		const record = node as { type?: string; content?: unknown[] };
		if (!Array.isArray(record.content)) return node;
		const kept = record.content.filter((child) => {
			const candidate = child as { type?: string; attrs?: Record<string, unknown> };
			const matches = candidate?.type === type && candidate.attrs?.[attr] === value;
			if (matches) removed = true;
			return !matches;
		});
		return { ...record, content: kept.map(prune) };
	};

	const pruned = prune(document);
	return removed ? JSON.stringify(pruned) : null;
}
